import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Badge,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Security as SecurityIcon,
  VpnKey as VpnKeyIcon,
  History as HistoryIcon,
  Analytics as AnalyticsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenIcon
} from '@mui/icons-material';
import api from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`gate-tabpanel-${index}`}
      aria-labelledby={`gate-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface Gate {
  _id: string;
  gateId: string;
  gateName: string;
  gateType: string;
  hardwareVendor: string;
  connectionType: string;
  status: {
    isOnline: boolean;
    currentState: string;
    lastPing?: string;
    batteryLevel?: number;
    signalStrength?: number;
    errorMessage?: string;
  };
  location: {
    _id: string;
    name: string;
    locationId: string;
  };
  statistics: {
    totalActivations: number;
    activationsToday: number;
    lastActivation?: string;
    averageResponseTime?: number;
  };
  isActive: boolean;
}

interface AccessCode {
  _id: string;
  codeId: string;
  code: string;
  codeType: string;
  status: string;
  validity: {
    startDate: string;
    endDate: string;
  };
  usage: {
    usageCount: number;
    maxUses?: number;
    lastUsed?: string;
  };
  guestInfo: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
  };
  reservation?: {
    confirmationNumber: string;
  };
  delivery: {
    method: string;
    smsDelivered: boolean;
    emailDelivered: boolean;
  };
}

interface AccessLog {
  _id: string;
  timestamp: string;
  accessMethod: string;
  result: string;
  gateAction: string;
  attemptData: {
    enteredCode?: string;
    licensePlate?: string;
  };
  guestInfo: {
    firstName?: string;
    lastName?: string;
  };
  responseTime: number;
  securityFlags: {
    isSuspiciousActivity: boolean;
    requiresInvestigation: boolean;
  };
}

const GateAccessPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [gates, setGates] = useState<Gate[]>([]);
  const [accessCodes, setAccessCodes] = useState<AccessCode[]>([]);
  const [accessLogs, setAccessLogs] = useState<AccessLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGate, setSelectedGate] = useState<string>('');
  const [openDialog, setOpenDialog] = useState('');

  // Dialog states
  const [gateDialog, setGateDialog] = useState(false);
  const [codeDialog, setCodeDialog] = useState(false);
  const [editingGate, setEditingGate] = useState<Gate | null>(null);

  useEffect(() => {
    fetchGates();
  }, []);

  useEffect(() => {
    if (selectedGate) {
      fetchAccessCodes();
      fetchAccessLogs();
    }
  }, [selectedGate]);

  const fetchGates = async () => {
    try {
      const response = await api.get('/gate-access/gates');
      const data = response.data;
      setGates(data.gates || []);
      if (data.gates && data.gates.length > 0 && !selectedGate) {
        setSelectedGate(data.gates[0]._id);
      }
    } catch (error) {
      console.error('Failed to fetch gates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccessCodes = async () => {
    if (!selectedGate) return;
    try {
      const response = await api.get(`/gate-access/gates/${selectedGate}/codes`);
      const data = response.data;
      setAccessCodes(data.codes || []);
    } catch (error) {
      console.error('Failed to fetch access codes:', error);
    }
  };

  const fetchAccessLogs = async () => {
    if (!selectedGate) return;
    try {
      const response = await api.get(`/gate-access/gates/${selectedGate}/logs?limit=100`);
      const data = response.data;
      setAccessLogs(data.logs || []);
    } catch (error) {
      console.error('Failed to fetch access logs:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const getStatusColor = (gate: Gate) => {
    if (!gate.status.isOnline) return 'error';
    if (gate.status.currentState === 'error') return 'warning';
    return 'success';
  };

  const getStatusIcon = (gate: Gate) => {
    if (!gate.status.isOnline) return <ErrorIcon />;
    if (gate.status.currentState === 'error') return <WarningIcon />;
    return <CheckCircleIcon />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const testGate = async (gateId: string) => {
    try {
      const response = await api.post(`/gate-access/gates/${gateId}/test`);
      const data = response.data;
      alert(data.message);
      fetchGates(); // Refresh gate data
    } catch (error) {
      console.error('Failed to test gate:', error);
      alert('Failed to test gate');
    }
  };

  const revokeAccessCode = async (codeId: string, reason: string) => {
    try {
      await api.post(`/gate-access/codes/${codeId}/revoke`, { reason });
      fetchAccessCodes(); // Refresh codes
    } catch (error) {
      console.error('Failed to revoke access code:', error);
    }
  };

  const resendAccessCode = async (codeId: string, method: string) => {
    try {
      await api.post(`/gate-access/codes/${codeId}/resend`, { method });
      fetchAccessCodes(); // Refresh codes
    } catch (error) {
      console.error('Failed to resend access code:', error);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" gutterBottom>
        <SecurityIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
        Gate Access Control
      </Typography>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
          <Tab icon={<SecurityIcon />} label="Gates Overview" />
          <Tab icon={<VpnKeyIcon />} label="Access Codes" />
          <Tab icon={<HistoryIcon />} label="Access Logs" />
          <Tab icon={<AnalyticsIcon />} label="Analytics" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          {/* Gates Overview */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Gate Status Overview</Typography>
            <Box>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchGates}
                sx={{ mr: 2 }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setGateDialog(true)}
              >
                Add Gate
              </Button>
            </Box>
          </Box>

          <Grid container spacing={3}>
            {gates.map((gate) => (
              <Grid item xs={12} md={6} lg={4} key={gate._id}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    border: selectedGate === gate._id ? '2px solid primary.main' : '1px solid divider'
                  }}
                  onClick={() => setSelectedGate(gate._id)}
                >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Typography variant="h6" component="div">
                        {gate.gateName}
                      </Typography>
                      <Badge
                        color={getStatusColor(gate)}
                        variant="dot"
                        sx={{ ml: 1 }}
                      >
                        {getStatusIcon(gate)}
                      </Badge>
                    </Box>

                    <Chip 
                      label={gate.gateType.replace('_', ' ').toUpperCase()}
                      variant="outlined"
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {gate.location.name}
                    </Typography>

                    <Typography variant="body2" gutterBottom>
                      <strong>Vendor:</strong> {gate.hardwareVendor.replace('_', ' ')}
                    </Typography>

                    <Typography variant="body2" gutterBottom>
                      <strong>Status:</strong> {gate.status.isOnline ? 'Online' : 'Offline'}
                    </Typography>

                    {gate.status.batteryLevel && (
                      <Typography variant="body2" gutterBottom>
                        <strong>Battery:</strong> {gate.status.batteryLevel}%
                      </Typography>
                    )}

                    <Typography variant="body2" gutterBottom>
                      <strong>Today's Access:</strong> {gate.statistics.activationsToday}
                    </Typography>

                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <Tooltip title="Test Gate">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            testGate(gate._id);
                          }}
                        >
                          <OpenIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Gate">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingGate(gate);
                            setGateDialog(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {/* Access Codes */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Access Codes</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCodeDialog(true)}
              disabled={!selectedGate}
            >
              Generate Code
            </Button>
          </Box>

          {!selectedGate ? (
            <Alert severity="info">Select a gate from the overview tab to manage access codes.</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Guest</TableCell>
                    <TableCell>Valid Period</TableCell>
                    <TableCell>Usage</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Delivery</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accessCodes.map((code) => (
                    <TableRow key={code._id}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {code.code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={code.codeType}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {code.guestInfo.firstName && code.guestInfo.lastName ? (
                          <div>
                            <Typography variant="body2">
                              {code.guestInfo.firstName} {code.guestInfo.lastName}
                            </Typography>
                            {code.reservation && (
                              <Typography variant="caption" color="text.secondary">
                                {code.reservation.confirmationNumber}
                              </Typography>
                            )}
                          </div>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No guest info
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          <Typography variant="caption" display="block">
                            Start: {formatDate(code.validity.startDate)}
                          </Typography>
                          <Typography variant="caption" display="block">
                            End: {formatDate(code.validity.endDate)}
                          </Typography>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {code.usage.usageCount}
                          {code.usage.maxUses && ` / ${code.usage.maxUses}`}
                        </Typography>
                        {code.usage.lastUsed && (
                          <Typography variant="caption" color="text.secondary">
                            Last: {formatDate(code.usage.lastUsed)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={code.status}
                          color={code.status === 'active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          {code.delivery.smsDelivered && (
                            <Chip label="SMS" size="small" color="success" sx={{ mr: 0.5, mb: 0.5 }} />
                          )}
                          {code.delivery.emailDelivered && (
                            <Chip label="Email" size="small" color="success" sx={{ mr: 0.5, mb: 0.5 }} />
                          )}
                          {!code.delivery.smsDelivered && !code.delivery.emailDelivered && (
                            <Chip label="Manual" size="small" color="default" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Resend Code">
                            <IconButton
                              size="small"
                              onClick={() => resendAccessCode(code._id, 'both')}
                              disabled={code.status !== 'active'}
                            >
                              <SendIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Revoke Code">
                            <IconButton
                              size="small"
                              onClick={() => revokeAccessCode(code._id, 'Manually revoked by admin')}
                              disabled={code.status !== 'active'}
                            >
                              <LockIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          {/* Access Logs */}
          <Typography variant="h6" gutterBottom>Access Logs</Typography>
          
          {!selectedGate ? (
            <Alert severity="info">Select a gate from the overview tab to view access logs.</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Guest</TableCell>
                    <TableCell>Attempt Data</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell>Gate Action</TableCell>
                    <TableCell>Response Time</TableCell>
                    <TableCell>Security</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accessLogs.map((log) => (
                    <TableRow key={log._id}>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(log.timestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={log.accessMethod.replace('_', ' ')}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {log.guestInfo.firstName && log.guestInfo.lastName ? (
                          <Typography variant="body2">
                            {log.guestInfo.firstName} {log.guestInfo.lastName}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Unknown
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          {log.attemptData.enteredCode && (
                            <Typography variant="caption" display="block" fontFamily="monospace">
                              Code: {log.attemptData.enteredCode}
                            </Typography>
                          )}
                          {log.attemptData.licensePlate && (
                            <Typography variant="caption" display="block">
                              Plate: {log.attemptData.licensePlate}
                            </Typography>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.result}
                          color={log.result === 'success' ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.gateAction}
                          color={log.gateAction === 'opened' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.responseTime}ms
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {log.securityFlags.isSuspiciousActivity && (
                          <Chip label="Suspicious" color="warning" size="small" />
                        )}
                        {log.securityFlags.requiresInvestigation && (
                          <Chip label="Investigation" color="error" size="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          {/* Analytics */}
          <Typography variant="h6" gutterBottom>Gate Analytics</Typography>
          <Typography variant="body2" color="text.secondary">
            Analytics dashboard coming soon. This will include:
          </Typography>
          <ul>
            <li>Daily/weekly/monthly access patterns</li>
            <li>Peak usage hours</li>
            <li>Success vs. failure rates</li>
            <li>Hardware performance metrics</li>
            <li>Security incident tracking</li>
          </ul>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default GateAccessPage;